import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { dictionary, Language } from "../utils/dictionary";

const VERSION_TOAST_ID = "fluxi-new-version";
const CHECK_INTERVAL_MS = 60_000;

let newVersionAvailable = false;

export const isNewVersionAvailable = () => newVersionAvailable;

const fetchDeployedVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    if (!response.ok) return null;
    const payload = (await response.json()) as { version?: string };
    return payload.version ?? null;
  } catch {
    return null;
  }
};

export function useVersionCheck() {
  const location = useLocation();
  const { language } = useAuth();
  const baselineRef = useRef<string | null>(null);
  const toastShownRef = useRef(false);

  const copy = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] ?? dictionary[key]?.[Language.SPANISH] ?? fallback;

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | null = null;

    const showUpdateToast = () => {
      if (toastShownRef.current) return;
      toastShownRef.current = true;
      toast.custom(
        (toastInstance) => (
          <div
            className={`${
              toastInstance.visible ? "animate-enter" : "animate-leave"
            } pointer-events-auto flex w-[min(360px,calc(100vw-32px))] items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg`}
          >
            <p className="text-sm leading-5 text-gray-800">{copy("app-new-version-message", "Hay una nueva versión de Fluxi")}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="shrink-0 rounded-full bg-gray-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800"
            >
              {copy("app-new-version-action", "Actualizar")}
            </button>
          </div>
        ),
        { id: VERSION_TOAST_ID, duration: Infinity }
      );
    };

    const checkVersion = async () => {
      if (document.visibilityState !== "visible") return;
      const version = await fetchDeployedVersion();
      if (cancelled || !version) return;
      if (baselineRef.current === null) {
        baselineRef.current = version;
        return;
      }
      if (version !== baselineRef.current) {
        newVersionAvailable = true;
        showUpdateToast();
      }
    };

    void checkVersion();
    intervalId = window.setInterval(() => {
      void checkVersion();
    }, CHECK_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      if (intervalId !== null) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [language]);

  useEffect(() => {
    if (!newVersionAvailable) return;
    const nextUrl = `${location.pathname}${location.search}${location.hash}`;
    window.location.assign(nextUrl);
  }, [location.pathname, location.search, location.hash]);
}
